import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  Grid,
  useMediaQuery,
  useTheme,
  TextField,
  Divider,
  InputAdornment,
  IconButton,
  Slider,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Alert,
  AlertTitle,
  Collapse,
  Popover,
  Switch,
  Link,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {
  beginProfileSaveBack,
  endProfileSaveBack,
  hasPendingHistoryLoad,
  isProfileSaveBackInProgress,
  useLinkedCalculator,
  type SharedBorrowerData,
  type SharedBorrowerFields,
  type SharedProceedsData,
} from '../../context/LinkedCalculatorContext';
import { useProfile } from '../../context/ProfileContext';
import type { CalculatorProfile } from '../../services/profileService';
import { useGlobalCalculatorSettings } from '../../context/GlobalCalculatorSettingsContext';
import HistoryToolbar, { type NewProfileDraft } from '../../components/HistoryToolbar';
import TextSizeControl from '../../components/TextSizeControl';
import ExportPdfButton from '../../components/ExportPdfButton';
import CurrencyTextField from '../../components/CurrencyTextField';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { logCalculatorInteraction, logPdfExport } from '../../services/analytics';
import {
  CalculatorTutorial,
  TutorialLaunchButton,
  useCalculatorTutorial,
  type TutorialStepDef,
} from '../../components/CalculatorTutorial';
import {
  CITIZENSHIP_LABELS,
  PROPERTY_COUNT_OPTIONS,
  getBsdBreakdown,
  type CitizenshipType,
  type PropertyCount,
} from '../../../utils/stampDutyRates';
import {
  DEFAULT_DECOUPLING_FEES,
  FEE_GUIDANCE,
  MSR_RATIO,
  TDSR_RATIO,
  computeExitingOwnerPosition,
  computeRemainingOwnerFinancing,
  computeTransferCosts,
  getDecouplingEligibility,
  getMaxTenureCap,
  getTenureCap,
  type DecouplingFees,
} from '../../utils/decouplingCalculator';
import { exportDecouplingToPDF } from '../../utils/decouplingCalculatorPdfExport';
import {
  DEFAULT_STATE,
  ageFromBirthYear,
  memberLabel,
  monthlyDebtOf,
  normalizeDecouplingState,
  propertiesOwnedToPropertyCount,
  toCalcInputs,
  type DecouplingState,
} from '../../utils/decouplingState';

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Same reference the Selling Calculator links from its SSD popover.
const IRAS_SSD_URL = 'https://www.iras.gov.sg/taxes/stamp-duty/for-property/selling-or-disposing-property/seller%27s-stamp-duty-%28ssd%29-for-residential-property';

function fmt(n: number): string {
  const rounded = Math.round(n);
  return rounded < 0
    ? `-S$${Math.abs(rounded).toLocaleString('en-SG')}`
    : `S$${rounded.toLocaleString('en-SG')}`;
}

/** Two-decimal currency, for monthly repayments where the cents matter. */
function fmtPrecise(n: number): string {
  return new Intl.NumberFormat('en-SG', {
    style: 'currency', currency: 'SGD', minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(n);
}

// Slider-draft helpers — same behaviour as the Buying Calculator's.
function snapToStep(value: number, min: number, max: number, step: number): number {
  const clamped = Math.min(max, Math.max(min, value));
  if (step <= 0) return clamped;
  const n = Math.round((clamped - min) / step);
  let snapped = min + n * step;
  const stepStr = step.toString();
  const dot = stepStr.indexOf('.');
  const decimals = dot === -1 ? 0 : stepStr.length - dot - 1;
  if (decimals > 0) snapped = Math.round(snapped * 10 ** decimals) / 10 ** decimals;
  return Math.min(max, Math.max(min, snapped));
}

function parseFlexibleNumber(s: string): number | null {
  const cleaned = s.replace(/,/g, '').replace(/\s/g, '').trim();
  if (cleaned === '' || cleaned === '-') return null;
  const n = parseFloat(cleaned.replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : null;
}

function formatDraftForEditing(v: number, step: number): string {
  if (step < 1 && step > 0) return String(Number(v.toFixed(2)));
  return String(Math.round(v));
}

function formatCurrencyDraftForInput(amount: number): string {
  return new Intl.NumberFormat('en-SG', {
    style: 'currency', currency: 'SGD', minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(amount);
}

// ─── Shared controls, ported from the Buying Calculator ───────────────────────

/** Label on the left, the value on the right with a pencil that swaps in a text field. */
const EditableSliderValueRow: React.FC<{
  label: React.ReactNode;
  value: number;
  min: number;
  max: number;
  step: number;
  onCommit: (v: number) => void;
  formatDisplay: (v: number) => string;
  valueColor: string;
  currencyInput?: boolean;
  commitMax?: number;
  disabled?: boolean;
}> = ({ label, value, min, max, step, onCommit, formatDisplay, valueColor, currencyInput = false, commitMax, disabled }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const skipBlurRef = useRef(false);

  const commit = () => {
    if (skipBlurRef.current) { skipBlurRef.current = false; return; }
    const raw = parseFlexibleNumber(draft);
    if (raw !== null) onCommit(snapToStep(raw, min, commitMax ?? max, step));
    setEditing(false);
  };

  const startEdit = () => {
    skipBlurRef.current = false;
    setDraft(currencyInput ? formatCurrencyDraftForInput(Math.round(value)) : formatDraftForEditing(value, step));
    setEditing(true);
  };

  const handleDraftChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (currencyInput) {
      const digits = e.target.value.replace(/\D/g, '');
      if (digits === '') { setDraft(''); return; }
      const n = parseInt(digits, 10);
      if (!Number.isFinite(n)) return;
      setDraft(formatCurrencyDraftForInput(n));
      return;
    }
    setDraft(e.target.value);
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, gap: 1, flexWrap: 'wrap' }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>{label}</Box>
      {editing ? (
        <TextField
          size="small"
          value={draft}
          onChange={handleDraftChange}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
            if (e.key === 'Escape') { e.preventDefault(); skipBlurRef.current = true; setEditing(false); }
          }}
          autoFocus
          sx={{ width: currencyInput ? { xs: 150, sm: 180 } : { xs: 130, sm: 150 } }}
          inputProps={{ inputMode: currencyInput ? 'numeric' : 'decimal' }}
        />
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, flexShrink: 0 }}>
          <Typography variant="body2" fontWeight={700} sx={{ color: valueColor }}>{formatDisplay(value)}</Typography>
          <IconButton size="small" onClick={startEdit} disabled={disabled} sx={{ p: 0.35, ml: 0.25 }}>
            <EditIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

const MOBILE_SLIDER_INTENT_THRESHOLD_PX = 8;

// On touch devices the knob stays inactive until the touch establishes horizontal
// intent, so taps and vertical scroll gestures over the track never move the value.
// Only touch-originated changes are gated — mouse and keyboard always pass through.
const IntentGuardedSlider: React.FC<React.ComponentProps<typeof Slider> & { isMobile: boolean }> = ({
  isMobile, onChange, sx, disabled, ...props
}) => {
  const isCoarsePointer = useMediaQuery('(pointer: coarse)');
  const guarded = isMobile || isCoarsePointer;
  const touchStateRef = useRef<{ startX: number; startY: number; intent: 'pending' | 'horizontal' | 'vertical' } | null>(null);
  const [dragReady, setDragReady] = useState(!guarded);

  useEffect(() => {
    setDragReady(!guarded);
    if (!guarded) touchStateRef.current = null;
  }, [guarded]);

  const resetTouch = () => {
    touchStateRef.current = null;
    if (guarded) setDragReady(false);
  };

  const handleTouchStart: React.TouchEventHandler<HTMLSpanElement> = (event) => {
    if (!guarded || disabled) return;
    const touch = event.touches[0];
    touchStateRef.current = { startX: touch.clientX, startY: touch.clientY, intent: 'pending' };
    setDragReady(false);
  };

  const handleTouchMove: React.TouchEventHandler<HTMLSpanElement> = (event) => {
    if (!guarded || disabled) return;
    const state = touchStateRef.current;
    if (!state) return;
    const touch = event.touches[0];
    const dx = Math.abs(touch.clientX - state.startX);
    const dy = Math.abs(touch.clientY - state.startY);
    if (state.intent === 'pending') {
      if (dx < MOBILE_SLIDER_INTENT_THRESHOLD_PX && dy < MOBILE_SLIDER_INTENT_THRESHOLD_PX) return;
      if (dx > dy) { state.intent = 'horizontal'; setDragReady(true); }
      else { state.intent = 'vertical'; setDragReady(false); }
      touchStateRef.current = state;
    }
  };

  const handleChange: NonNullable<React.ComponentProps<typeof Slider>['onChange']> = (event, value, activeThumb) => {
    if (!onChange) return;
    if (!guarded) { onChange(event, value, activeThumb); return; }
    const isTouchEvent = typeof event?.type === 'string' && event.type.startsWith('touch');
    if (isTouchEvent && touchStateRef.current?.intent !== 'horizontal') return;
    onChange(event, value, activeThumb);
  };

  return (
    <Slider
      {...props}
      disabled={disabled}
      onChange={handleChange}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={resetTouch}
      onTouchCancel={resetTouch}
      sx={[
        {
          touchAction: guarded ? 'pan-y' : 'auto',
          '& .MuiSlider-thumb': guarded
            ? {
                transition: 'transform 120ms ease',
                transform: dragReady ? 'translate(-50%, -50%) scale(1.05)' : 'translate(-50%, -50%) scale(1)',
              }
            : undefined,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    />
  );
};

/** One line of a tap-to-explain breakdown. Same shape as the Buying Calculator's. */
interface CalcDialogRow { label: string; value: string; isResult?: boolean; isSeparator?: boolean; note?: boolean; }
interface CalcDialogInfo { title: string; rows: CalcDialogRow[]; }
// ─── State ────────────────────────────────────────────────────────────────────

// ─── Small presentational pieces ──────────────────────────────────────────────

const SectionCardHeader: React.FC<{
  title: string;
  step?: number;
  /** Right-hand hint, in the slot "Step n" uses — what the card is about, once the
   *  title itself is given over to naming the person and their role. */
  note?: string;
  color: string;
  isDark: boolean;
  cardBorder: string;
}> = ({ title, step, note, color, isDark, cardBorder }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    px: 2.5, py: 1.5,
    bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'grey.100',
    borderBottom: `1px solid ${cardBorder}`,
  }}>
    <Typography variant="subtitle1" fontWeight={800} sx={{
      color, letterSpacing: '0.08em', fontSize: '0.95rem', textTransform: 'uppercase',
    }}>
      {title}
    </Typography>
    {step !== undefined ? (
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.85rem' }}>
        Step {step}
      </Typography>
    ) : note ? (
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.85rem' }}>
        {note}
      </Typography>
    ) : null}
  </Box>
);

/** Currency field that keeps its own formatted display while the user types. */
const MoneyField: React.FC<{
  label: string;
  value: number;
  onChange: (v: number) => void;
  helperText?: string;
  disabled?: boolean;
}> = ({ label, value, onChange, helperText, disabled }) => {
  const [display, setDisplay] = useState(() => (value === 0 ? '' : Math.round(value).toLocaleString('en-SG')));

  useEffect(() => {
    setDisplay(value === 0 ? '' : Math.round(value).toLocaleString('en-SG'));
  }, [value]);

  return (
    <TextField
      size="small"
      fullWidth
      disabled={disabled}
      label={label}
      value={display}
      helperText={helperText}
      onChange={(e) => {
        const digits = e.target.value.replace(/[^0-9]/g, '');
        const next = digits === '' ? 0 : parseInt(digits, 10);
        setDisplay(digits === '' ? '' : next.toLocaleString('en-SG'));
        onChange(Number.isFinite(next) ? next : 0);
      }}
      inputProps={{ inputMode: 'numeric' }}
      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
      sx={{ mb: 2 }}
    />
  );
};

/** One line in a results card. */
// ─── Tutorial ─────────────────────────────────────────────────────────────────

const DECOUPLING_TUTORIAL_STEPS: TutorialStepDef[] = [
  {
    title: 'Welcome to the Decoupling Calculator',
    content:
      'Decoupling is a sale of a part-share between two owners of the same property. One owner — the SELLER — sells their share to the other — the BUYER — who ends up holding 100%.\n\n' +
      'The point is what happens next: the seller\'s name comes off the title, so their next purchase is assessed as a FIRST property. For a Singapore Citizen that is 0% ABSD instead of 20%.\n\n' +
      'This calculator answers two questions: what does the sale cost each side, and can the buyer carry the property on their own income?',
    targetElement: null,
  },
  {
    title: 'Private property only',
    content:
      'This page covers private property, because that is the only kind that can be decoupled.\n\n' +
      '• HDB — not permitted for married couples since 1 Apr 2016, bar a short list of exceptions (divorce, death of an owner, documented hardship, loss of citizenship, a court order). The usual alternative is to sell on the open market and buy the next property in one name\n' +
      '• EC — only once the 5-year MOP has run, at which point it is private property and this calculation applies as it stands. Watch the SSD clock: it runs from key collection, not from the end of MOP, so an EC can clear MOP and still sit inside the SSD window',
    targetElement: null,
  },
  {
    title: 'Property',
    content:
      'Enter the current market value — the bank will order a fresh valuation, and that value is what the duties are charged on, not the original purchase price.\n\n' +
      'Outstanding loan is the full joint balance — it is discharged in its entirety on completion and replaced by a new loan in one name.\n\n' +
      'The transfer date is completion; eight to twelve weeks out is typical.',
    targetElement: '[data-tutorial="property-section"]',
  },
  {
    title: 'Seller',
    content:
      'Set who is selling and what share they sell. A 50-50 split is the common case; the buyer\'s existing share fills in automatically.\n\n' +
      'The seller\'s CPF figure is the amount drawn for this property PLUS accrued interest — the number on their CPF statement, not the original withdrawal. That refund goes back to their own OA on completion and cannot be taken as cash.\n\n' +
      'Only the seller is asked for here. Everything about the buyer — including their own CPF already in the property — sits together in Step 3.',
    targetElement: '[data-tutorial="ownership-section"]',
  },
  {
    title: 'Buyer financing — where most plans die',
    content:
      'The joint loan is discharged on completion and replaced by a new facility in one name. A mortgage two salaries chose must now clear TDSR on one, stressed at the MAS 4% floor rather than the contracted rate.\n\n' +
      'Age sets the tenure cap: 30 years, or up to age 65, whichever binds first. Dragging the tenure past that is allowed but moves the loan into the extended regime and drops the LTV ceiling from 75% to 55% — the calculator asks you to confirm before it does.\n\n' +
      'Bank Rate is the rate actually offered, and it drives the monthly repayment you quote the client. TDSR is still assessed at the 4% stress floor regardless, so the two figures differ on purpose.\n\n' +
      'Two CPF figures sit next to each other here, and they are not the same thing: CPF OA Balance is what the buyer can put in to shrink the new loan, while CPF used + accrued interest is what they already sank into this property — it stays where it is and simply appears in the workings.\n\n' +
      'The buyer\'s property count drives ABSD. Leave it at 1st Property when this becomes their only one — then no ABSD is payable, which is the whole reason decoupling is cheaper than buying a second property outright.\n\n' +
      'Every slider here can be typed into instead — tap the pencil beside the value.',
    targetElement: '[data-tutorial="financing-section"]',
  },
  {
    title: 'Seller Stamp Duty',
    content:
      'SSD is payable when a property is disposed of inside the holding period, and a decoupling counts — it is taxed like an early sale, on the share that moves.\n\n' +
      'Switch it on and enter the purchase date: the date the seller acquired this property, or key collection for an EC. The transfer date in Step 1 closes the holding period, and the band applied is shown under the calculated figure.\n\n' +
      'For purchases on or after 4 July 2025 the rates run 16%, 12%, 8% and 4% across the first four years. Purchases through 3 July 2025 keep the 12%, 8%, 4% three-year schedule.\n\n' +
      'Leave it off when the property is comfortably outside the window — on a recently bought property, SSD alone can outweigh the ABSD the decoupling was meant to save.',
    targetElement: '[data-tutorial="ssd-section"]',
  },
  {
    title: 'Reading the result — and tapping any figure for its working',
    content:
      'Three cards on the right:\n\n' +
      '• Cost to Decouple — BSD and ABSD on the share (paid by the buyer), SSD on the share (paid by the seller), plus legal and bank fees, split by who bears them\n' +
      '• <buyer> — Buyer — their sole-name loan, with a PASS or SHORTFALL banner. A shortfall is the gap between the loan needed and what the bank will actually lend\n' +
      '• <seller> — Seller — what they walk away with: cash in hand, and CPF restored to their OA\n\n' +
      'Once you type the names in, each card is headed with that person and their role, so a client can see at a glance which half of the page is theirs.\n\n' +
      'Any row with a small circled i beside it opens the formula and the numbers behind it — the BSD tiers, how each loan ceiling was derived, how TDSR was applied, where the seller\'s cash went. Use it to show a client why a figure is what it is, or to check your own inputs when a number looks wrong.\n\n' +
      'A negative net cash figure is a real outcome on a heavily geared property: the loan share and CPF refund together can exceed the share value, leaving the owner to top up on completion.',
    targetElement: '[data-tutorial="results"]',
  },
  {
    title: 'Carry it into the Buying Calculator',
    content:
      'Once the numbers work, move to the Buying Calculator using the arrows at the top of the page or the Calculators menu.\n\n' +
      'The seller\'s cash proceeds and CPF refund travel with you as the funds for their next purchase — and they arrive as the ONLY borrower, so the purchase is priced at first-property ABSD rates. That is the whole point of the exercise.\n\n' +
      'Save to History first if you want to come back to this case; history is shared across all the Super Calculators.',
    targetElement: '[data-tutorial="load-example"]',
  },
];

// ─── Main Component ───────────────────────────────────────────────────────────

export default function DecouplingCalculator() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDark = theme.palette.mode === 'dark';
  const linkedCalc = useLinkedCalculator();
  const { selectedProfile, getSelectedName, updateProfile } = useProfile();
  const currentUser = useCurrentUser();
  // Always the bank stress floor: HDB is blocked here, and a post-MOP EC refinances
  // as a private bank loan, so the HDB rate never applies.
  const { settings } = useGlobalCalculatorSettings();
  const stressTestRate = settings.stressTestRate;

  const cardBg = isDark ? 'rgba(255,255,255,0.03)' : '#f9fafb';
  const cardBorder = isDark ? 'rgba(255,255,255,0.08)' : '#e5e7eb';

  const [state, setState] = useState<DecouplingState>(DEFAULT_STATE);
  const [splitScroll, setSplitScroll] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [calcDialog, setCalcDialog] = useState<CalcDialogInfo | null>(null);
  const [stretchDialogOpen, setStretchDialogOpen] = useState(false);
  const [pendingTenure, setPendingTenure] = useState<number | null>(null);

  // ── Role assignment for a loaded profile ──────────────────────────────────
  // A decoupling has two sides and a saved group is just an unordered list of people,
  // so the calculator cannot know who is giving up their share. Rather than guess, it
  // asks once per selection and populates from the answer.
  const [roleMembers, setRoleMembers] = useState<CalculatorProfile[]>([]);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [ssdInfoAnchor, setSsdInfoAnchor] = useState<HTMLElement | null>(null);
  const [roleExitingId, setRoleExitingId] = useState<number | null>(null);
  const [roleRemainingId, setRoleRemainingId] = useState<number | null>(null);
  /** Which side a lone profile sits on — the only question worth asking for one person. */
  const [roleSingleChoice, setRoleSingleChoice] = useState<'exiting' | 'remaining'>('remaining');

  const tutorial = useCalculatorTutorial('decouplingCalculatorTutorialProgress', DECOUPLING_TUTORIAL_STEPS.length);

  const update = useCallback(<K extends keyof DecouplingState>(key: K, value: DecouplingState[K]) => {
    setState((prev) => ({ ...prev, [key]: value }));
  }, []);

  const updateFee = useCallback((key: keyof DecouplingFees, value: number) => {
    setState((prev) => ({ ...prev, fees: { ...prev.fees, [key]: value } }));
  }, []);

  // ── Results ───────────────────────────────────────────────────────────────
  // The stress-test rate is a global setting rather than page state, so it is layered
  // on here instead of living in DecouplingState.
  const calcInputs = useMemo(() => {
    const inputs = toCalcInputs(state);
    inputs.financing.stressTestRate = stressTestRate;
    return inputs;
  }, [state, stressTestRate]);

  const eligibility = useMemo(() => getDecouplingEligibility(calcInputs), [calcInputs]);
  const costs = useMemo(() => computeTransferCosts(calcInputs), [calcInputs]);
  const financing = useMemo(() => computeRemainingOwnerFinancing(calcInputs, costs), [calcInputs, costs]);
  const exitPosition = useMemo(() => computeExitingOwnerPosition(calcInputs, costs), [calcInputs, costs]);

  // A decoupling is a sale of one owner's share to the other, so both sides are named
  // for what they actually are: the exiting owner sells, the remaining owner buys.
  // (The state keys stay `exiting*` / `remaining*` — they are persisted in every saved
  // history snapshot and renaming them would orphan existing entries.)
  const sellerLabel = state.exitingName.trim() || 'Seller';
  const buyerLabel = state.remainingName.trim() || 'Buyer';
  /** Card headings: "Ming — Seller" once named, plain "Seller" until then. */
  const sellerHeading = state.exitingName.trim() ? `${state.exitingName.trim()} — Seller` : 'Seller';
  const buyerHeading = state.remainingName.trim() ? `${state.remainingName.trim()} — Buyer` : 'Buyer';
  const remainingSharePct = Math.max(0, 100 - state.exitingSharePct);

  // ── Tenure caps (MAS), by the remaining owner's age ────────────────────────
  //   Standard: max age 65 / 30 yrs — keeps the full 75% LTV.
  //   Extended: max age 75 / 35 yrs — allowed, but running past the standard cap drops
  //     the max LTV to 55% (the agent confirms via a dialog).
  // getMaxTenureCap is shared with the maths module, so the slider and the calculation
  // cannot disagree about what is allowed.
  const standardTenureCap = useMemo(() => getTenureCap(state.age, false), [state.age]);
  const maxTenureCap = useMemo(() => getMaxTenureCap(state.age, state.tab), [state.age, state.tab]);
  const tenureStretched = state.tenure > standardTenureCap;

  // Clamp the tenure down whenever the cap tightens (age change, tab switch).
  useEffect(() => {
    if (maxTenureCap > 0 && state.tenure > maxTenureCap) update('tenure', maxTenureCap);
  }, [maxTenureCap, state.tenure, update]);

  // Re-default the tenure to the longest that still keeps the best LTV whenever the age
  // moves, so an age edit lifts or lowers the tenure with it instead of sticking.
  const prevAgeRef = useRef(state.age);
  const restoringRef = useRef(false);
  useEffect(() => {
    if (prevAgeRef.current === state.age) return;
    prevAgeRef.current = state.age;
    if (restoringRef.current) return; // keep the restored tenure on a history load
    const defaultTenure = Math.max(1, state.tab === 'hdb' ? maxTenureCap : standardTenureCap);
    if (state.tenure !== defaultTenure) update('tenure', defaultTenure);
  }, [state.age, state.tab, state.tenure, maxTenureCap, standardTenureCap, update]);

  // Intercept tenure changes: confirm before crossing into the 55% LTV regime.
  const requestTenureChange = useCallback((newTenure: number) => {
    const clamped = Math.min(Math.max(1, newTenure), Math.max(1, maxTenureCap));
    if (clamped > standardTenureCap && !tenureStretched) {
      setPendingTenure(clamped);
      setStretchDialogOpen(true);
      return;
    }
    update('tenure', clamped);
  }, [maxTenureCap, standardTenureCap, tenureStretched, update]);

  // ── Profile loading ───────────────────────────────────────────────────────
  // Writes one member onto each side of the transfer. The exiting owner contributes only
  // their identity and CPF — their income is never used here — while the remaining owner
  // fills the whole borrower card, since theirs is the income the new loan is tested on.
  const applyRoles = useCallback((exiting: CalculatorProfile | null, remaining: CalculatorProfile | null) => {
    setState((prev) => {
      const next = { ...prev };
      next.exitingProfileId = exiting?.Id ?? null;
      next.remainingProfileId = remaining?.Id ?? null;
      if (exiting) {
        next.exitingName = exiting.Name?.trim() || '';
        next.exitingCitizenship = (exiting.Citizenship as CitizenshipType) || 'SC';
        next.exitingCpf = Math.round(exiting.Cpf ?? 0);
      }
      if (remaining) {
        next.remainingName = remaining.Name?.trim() || '';
        next.remainingCitizenship = (remaining.Citizenship as CitizenshipType) || 'SC';
        next.remainingCpf = Math.round(remaining.Cpf ?? 0);
        next.remainingPropertyCount = propertiesOwnedToPropertyCount(remaining.PropertiesOwned);
        next.age = ageFromBirthYear(remaining.BirthYear);
        next.monthlyIncome = Math.round(remaining.MonthlyIncome ?? 0);
        next.otherAnnualIncome = Math.round(remaining.OtherAnnualIncome ?? 0);
        next.isSelfEmployed = Boolean(remaining.SelfEmployedInd);
        next.existingMonthlyDebt = monthlyDebtOf(remaining);
        next.cashAvailable = Math.round(remaining.Saving ?? 0);
        next.cpfAvailable = Math.round(remaining.Cpf ?? 0);
      }
      return next;
    });
  }, []);

  // Which selection the roles were last resolved for. Keyed by the member ids so a plain
  // refresh of the same group (a profile edit saved elsewhere) doesn't re-prompt, but
  // genuinely switching client does.
  const assignedProfileKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!selectedProfile) {
      assignedProfileKeyRef.current = null;
      setRoleMembers([]);
      return;
    }

    const members = selectedProfile.type === 'profile'
      ? [selectedProfile.profile]
      : (selectedProfile.group.profiles || []);
    if (members.length === 0) return;

    const key = `${selectedProfile.type}-${selectedProfile.id}:${members.map((m) => m.Id).join(',')}`;
    const switched = assignedProfileKeyRef.current !== key;
    // Claimed before the guards below, so a snapshot restore doesn't leave the key stale
    // and re-prompt on the next unrelated render.
    assignedProfileKeyRef.current = key;
    setRoleMembers(members);

    if (!switched) return;
    // A queued Profile Manager load is about to restore a full saved snapshot — let it win.
    if (hasPendingHistoryLoad()) return;
    // A save-back just wrote these values to the profile; the refreshed selection that
    // follows is not a new client.
    if (isProfileSaveBackInProgress()) return;

    if (members.length === 1) {
      // One person fills one side, but which one is still the agent's call — they may be
      // the client buying their spouse out, or the one being bought out.
      setRoleSingleChoice('remaining');
      setRoleRemainingId(members[0].Id);
      setRoleExitingId(null);
      setRoleDialogOpen(true);
      return;
    }

    // Default to the group's own order — first member keeps the property — then ask.
    setRoleRemainingId(members[0].Id);
    setRoleExitingId(members[1].Id);
    setRoleDialogOpen(true);
  }, [selectedProfile, applyRoles]);

  const confirmRoles = useCallback(() => {
    if (roleMembers.length === 1) {
      const only = roleMembers[0];
      applyRoles(
        roleSingleChoice === 'exiting' ? only : null,
        roleSingleChoice === 'remaining' ? only : null,
      );
      setRoleExitingId(roleSingleChoice === 'exiting' ? only.Id : null);
      setRoleRemainingId(roleSingleChoice === 'remaining' ? only.Id : null);
      setRoleDialogOpen(false);
      return;
    }
    const exiting = roleMembers.find((m) => m.Id === roleExitingId) ?? null;
    const remaining = roleMembers.find((m) => m.Id === roleRemainingId) ?? null;
    applyRoles(exiting, remaining);
    setRoleDialogOpen(false);
  }, [roleMembers, roleExitingId, roleRemainingId, roleSingleChoice, applyRoles]);

  // ── Writing back to the profile ───────────────────────────────────────────
  // "Save to History and Profile" pushes the borrower fields back onto the people they
  // came from. The remaining owner carries everything the loan was assessed on; the
  // exiting owner only ever contributes identity here, so only that is written for them.
  const toPropertiesOwned = (count: PropertyCount): number =>
    count === '3rd+' ? 3 : count === '2nd' ? 2 : 1;

  // The calculator holds one debt figure while a profile itemises four. Leave the
  // breakdown alone when the total still agrees; park a changed total under OtherLoan
  // rather than silently discarding the client's car loan and credit card lines.
  const debtFieldsFor = (member: CalculatorProfile, totalDebt: number) => {
    const existing = monthlyDebtOf(member);
    return existing === Math.round(totalDebt)
      ? {}
      : { CarLoan: 0, HousingLoan: 0, CreditCard: 0, OtherLoan: Math.round(totalDebt) };
  };

  const saveInputsToProfile = useCallback(async (): Promise<boolean> => {
    if (!selectedProfile) return false;
    const members = selectedProfile.type === 'profile'
      ? [selectedProfile.profile]
      : (selectedProfile.group.profiles || []);
    if (members.length === 0) return false;

    // Fall back to the group's own order only when no roles were ever assigned.
    const remainingMember = members.find((m) => m.Id === state.remainingProfileId)
      ?? (state.remainingProfileId === null && state.exitingProfileId === null ? members[0] : undefined);
    const exitingMember = members.find((m) => m.Id === state.exitingProfileId);

    beginProfileSaveBack();
    try {
      if (remainingMember) {
        await updateProfile(remainingMember.Id, {
          Name: state.remainingName.trim() || remainingMember.Name,
          BirthYear: new Date().getFullYear() - state.age,
          MonthlyIncome: Math.round(state.monthlyIncome),
          OtherAnnualIncome: Math.round(state.otherAnnualIncome),
          SelfEmployedInd: state.isSelfEmployed ? 1 : 0,
          ...debtFieldsFor(remainingMember, state.existingMonthlyDebt),
          Citizenship: state.remainingCitizenship,
          PropertiesOwned: toPropertiesOwned(state.remainingPropertyCount),
          Saving: Math.round(state.cashAvailable),
          Cpf: Math.round(state.cpfAvailable),
        });
      }

      if (exitingMember) {
        // Deliberately narrow. The exiting owner's CPF figure on this page is the amount
        // sunk into THIS property plus accrued interest — not their OA balance — so
        // writing it to Cpf would corrupt the profile for every other calculator.
        await updateProfile(exitingMember.Id, {
          Name: state.exitingName.trim() || exitingMember.Name,
          Citizenship: state.exitingCitizenship,
        });
      }
      return true;
    } catch (err) {
      console.error('Failed to save inputs to profile:', err);
      return false;
    } finally {
      endProfileSaveBack();
    }
  }, [selectedProfile, state, updateProfile]);

  /** Seeds "Save to Profile" when no profile is loaded yet. */
  const buildNewProfilesFromInputs = useCallback((): NewProfileDraft | null => {
    const members: NewProfileDraft['members'] = [];

    if (state.remainingName.trim() || state.monthlyIncome > 0) {
      members.push({
        Name: state.remainingName.trim(),
        BirthYear: new Date().getFullYear() - state.age,
        MonthlyIncome: Math.round(state.monthlyIncome),
        OtherAnnualIncome: Math.round(state.otherAnnualIncome),
        Saving: Math.round(state.cashAvailable),
        Cpf: Math.round(state.cpfAvailable),
        // Only the total is known here — park it under OtherLoan.
        CarLoan: 0,
        HousingLoan: 0,
        CreditCard: 0,
        OtherLoan: Math.round(state.existingMonthlyDebt),
        SelfEmployedInd: state.isSelfEmployed ? 1 : 0,
        Citizenship: state.remainingCitizenship,
        PropertiesOwned: toPropertiesOwned(state.remainingPropertyCount),
      });
    }

    if (state.exitingName.trim()) {
      // Identity only — this page never captures the exiting owner's income.
      members.push({ Name: state.exitingName.trim(), Citizenship: state.exitingCitizenship });
    }

    if (members.length === 0) return null;
    return {
      members,
      groupName: [state.remainingName.trim(), state.exitingName.trim()].filter(Boolean).join(' & '),
      relationship: 'SPOUSE',
    };
  }, [state]);

  // ── Linked calculator: state snapshot ─────────────────────────────────────
  const snapshotRef = useRef<DecouplingState>(state);
  snapshotRef.current = state;

  useEffect(() => {
    linkedCalc.registerStateGetter('decoupling', () => snapshotRef.current);
  }, [linkedCalc]);

  useEffect(() => {
    linkedCalc.registerStateSetter('decoupling', (restored: unknown) => {
      if (!restored) return;
      // Suppress the age→tenure re-default so the saved tenure survives the load.
      restoringRef.current = true;
      const next = normalizeDecouplingState(restored);
      prevAgeRef.current = next.age;
      setState(next);
      window.setTimeout(() => { restoringRef.current = false; }, 0);
    });
  }, [linkedCalc]);

  useEffect(() => {
    // No borrower profiles are authored here — the Maximizer and Buying Calculator own
    // that. Returning empty lets their builders win in saveAllToHistory's preference loop.
    linkedCalc.registerProfileBuilder('decoupling', () => []);
  }, [linkedCalc]);

  // ── Borrower sync ─────────────────────────────────────────────────────────
  // Two calculators upstream send a primary and an optional additional borrower. Which
  // of them is leaving is this page's question, not theirs, so the sides are resolved by
  // name against whatever role assignment is already in place — a bare primary → remaining
  // mapping would silently undo the agent's answer to the role dialog on every navigation.
  const incomingBorrowerRef = useRef<SharedBorrowerData | null>(null);
  /** The exiting owner's income fields, as resolved on the last inbound sync. */
  const exitingBorrowerRef = useRef<SharedBorrowerFields | null>(null);

  useEffect(() => {
    linkedCalc.registerBorrowerApplier('decoupling', (data: SharedBorrowerData) => {
      incomingBorrowerRef.current = data;
      setState((prev) => {
        let remaining: SharedBorrowerFields | null = data.primary;
        let exiting = data.includeAdditional ? data.additional : null;

        // If the sender's primary is the person already marked as leaving here, the two
        // sides are the other way round from the default mapping.
        const assignedExiting = prev.exitingName.trim().toLowerCase();
        if (assignedExiting && data.primary.name?.trim().toLowerCase() === assignedExiting) {
          if (exiting) {
            remaining = data.additional;
            exiting = data.primary;
          } else {
            // A lone borrower who is the one leaving — which is exactly what comes back
            // when the agent fills the seller's income in on the Buying Calculator and
            // returns. Keep them on the seller's side and leave the buyer's own figures
            // alone, so the round trip doesn't overwrite one person with the other.
            exiting = data.primary;
            remaining = null;
          }
        }
        exitingBorrowerRef.current = exiting;

        const withExiting = { ...prev, exitingName: exiting?.name?.trim() || prev.exitingName };
        if (!remaining) return withExiting;

        return {
          ...withExiting,
          age: remaining.age || prev.age,
          monthlyIncome: remaining.monthlyIncome || prev.monthlyIncome,
          otherAnnualIncome: remaining.otherAnnualIncome ?? prev.otherAnnualIncome,
          isSelfEmployed: remaining.isSelfEmployed,
          existingMonthlyDebt: remaining.existingMonthlyDebt ?? prev.existingMonthlyDebt,
          remainingName: remaining.name?.trim() || prev.remainingName,
          cashAvailable: remaining.cash ?? prev.cashAvailable,
          cpfAvailable: remaining.cpf ?? prev.cpfAvailable,
        };
      });
    });
  }, [linkedCalc]);

  // Read fresh on every navigation, so a ref rather than a closure.
  //
  // Whatever the branch, includeAdditional goes out false: that is the one fact this page
  // always knows. After the transfer only one name is on the title, so the Buying
  // Calculator must assess a single borrower and price the next purchase at
  // first-property ABSD rates — the whole point of decoupling.
  //
  // Cash and CPF are deliberately absent here: the proceeds channel below carries the
  // exiting owner's funds and matches them to the right borrower by name.
  const borrowerOutRef = useRef<SharedBorrowerData | null>(null);
  borrowerOutRef.current = (() => {
    const remainingFromState = {
      age: state.age,
      monthlyIncome: state.monthlyIncome,
      otherAnnualIncome: state.otherAnnualIncome,
      isSelfEmployed: state.isSelfEmployed,
      existingMonthlyDebt: state.existingMonthlyDebt,
      name: state.remainingName.trim() || undefined,
    };
    const exitingName = state.exitingName.trim() || undefined;
    const exiting = exitingBorrowerRef.current;

    if (exiting) {
      // The exiting owner's income is known: they lead, because they are the one buying next.
      return {
        primary: { ...exiting, name: exitingName ?? exiting.name },
        additional: remainingFromState,
        includeAdditional: false,
      };
    }

    const incoming = incomingBorrowerRef.current;
    if (!exitingName && incoming) {
      // The seller has no name here, so there is nothing to tell the two sides apart by.
      // Echo what arrived rather than guessing — sent straight back to Buying it is a
      // no-op, where zeroing would wipe figures the agent typed there.
      return { ...incoming, includeAdditional: false };
    }

    // Their income never reached this page — only the buyer's is captured here, since the
    // new loan is tested on them. The seller still goes out as the borrower, with the
    // income left blank for the agent to fill: sending the buyer's salary under the
    // seller's name would read as fact on the next card, and sending the buyer instead
    // prices their next purchase at the wrong ABSD rate, which is the whole exercise.
    return {
      primary: {
        age: 0,
        monthlyIncome: 0,
        otherAnnualIncome: 0,
        isSelfEmployed: false,
        existingMonthlyDebt: 0,
        name: exitingName,
      },
      additional: remainingFromState,
      includeAdditional: false,
    };
  })();

  useEffect(() => {
    linkedCalc.registerBorrowerExtractor('decoupling', () => borrowerOutRef.current!);
  }, [linkedCalc]);

  // ── Proceeds sync (Decoupling → Buying) ───────────────────────────────────
  // Reuses the Selling Calculator's channel verbatim: one "seller" — the exiting
  // owner — with their cash proceeds and their CPF refund.
  const proceedsRef = useRef<SharedProceedsData>({ netSalesProceeds: 0, cpfWithdrawal: 0, soldHdb: false });
  proceedsRef.current = {
    netSalesProceeds: Math.max(0, Math.round(exitPosition.netCash)),
    cpfWithdrawal: Math.max(0, Math.round(exitPosition.cpfRefund)),
    // A decoupling is never an HDB disposal, so no EC resale levy is implied.
    soldHdb: false,
    sellers: [{
      name: state.exitingName.trim() || undefined,
      cashProceeds: Math.max(0, Math.round(exitPosition.netCash)),
      cpfRefund: Math.max(0, Math.round(exitPosition.cpfRefund)),
    }],
  };

  useEffect(() => {
    linkedCalc.registerProceedsExtractor('decoupling', () => proceedsRef.current);
  }, [linkedCalc]);

  // ── Clear ─────────────────────────────────────────────────────────────────
  const clearCalculator = useCallback(() => {
    setState({ ...DEFAULT_STATE, fees: { ...DEFAULT_DECOUPLING_FEES } });
    incomingBorrowerRef.current = null;
    exitingBorrowerRef.current = null;
    prevAgeRef.current = DEFAULT_STATE.age;
    setRoleDialogOpen(false);
    setCalcDialog(null);

    // Drop the loaded example/history entry too. Without this the toolbar keeps showing
    // the entry that was loaded and the reset reads as if nothing happened. Saved
    // history entries themselves are untouched.
    linkedCalc.setSelectedExample('');
    linkedCalc.setActiveSelection(null);
    linkedCalc.showToast('Decoupling Calculator reset');

    setClearDialogOpen(false);
    logCalculatorInteraction('decoupling_calculator', 'clear');
  }, [linkedCalc]);

  // ── PDF ───────────────────────────────────────────────────────────────────
  const handleExportPdf = useCallback(() => {
    logPdfExport('decoupling_calculator', currentUser);
    return exportDecouplingToPDF({
      state: {
        tab: state.tab,
        marketValue: state.marketValue,
        outstandingLoan: state.outstandingLoan,
        ssdApplies: state.ssdApplies,
        purchaseDate: state.purchaseDate,
        transferDate: state.transferDate,
        exitingName: sellerLabel,
        remainingName: buyerLabel,
        exitingSharePct: state.exitingSharePct,
        remainingSharePct,
        bankRate: state.interestRate,
        stressTestRate,
      },
      costs,
      financing,
      exitPosition,
      guards: eligibility.guards,
      agentInfo: currentUser ?? undefined,
      // Heads the cover's "Specially Prepared for" line; the report falls back to the
      // two names on the transfer when no profile is loaded.
      profileName: selectedProfile ? getSelectedName() ?? undefined : undefined,
    }).catch((err) => {
      // eslint-disable-next-line no-console
      console.error('Failed to export decoupling calculator PDF', err);
    });
  }, [
    state, costs, financing, exitPosition, eligibility.guards,
    currentUser, sellerLabel, buyerLabel, remainingSharePct, stressTestRate,
    selectedProfile, getSelectedName,
  ]);

  // ── Styling ───────────────────────────────────────────────────────────────
  // Private is the only property type this page handles, so the accent is fixed.
  const tabColor = '#1e3a5f';
  const highlightColor = '#D97706';
  const sectionCardSx = { mb: 2, overflow: 'hidden' as const, borderRadius: 2, borderColor: cardBorder };
  const sectionBodySx = { p: 2.5 };
  const columnScrollSx = (splitScroll && !isMobile)
    ? { maxHeight: 'calc(100vh - 112px)', overflowY: 'auto' as const, pr: 1 }
    : undefined;

  const blocked = eligibility.blocked;
  // Everything downstream derives from the market value. The cards still render at zero
  // before it is entered — but the loan verdict holds, because a zero required loan
  // against a zero ceiling would otherwise read as a green "can carry it alone".
  const loanAssessable = state.marketValue > 0;

  // The make-up of the new loan, as one line under the headline figure: the seller's
  // share funded to the LTV, plus the buyer's own slice of the old loan being redeemed,
  // less any own funds left after the downpayment. The full working is in the dialog.
  const loanMakeup = [
    `${fmt(financing.buyoutFinanced)} of the seller's share at ${Math.round(financing.ltvPct * 100)}% LTV`,
    `+ ${fmt(financing.ownLoanShare)} of the old loan redeemed (${remainingSharePct}%)`,
    financing.ownFundsApplied > financing.buyoutDownpayment
      ? `− ${fmt(financing.ownFundsApplied - financing.buyoutDownpayment)} own funds after the downpayment`
      : null,
  ].filter(Boolean).join(' ');

  /** Matches the weight and size of the Buying Calculator's borrower field labels. */
  const borrowerFieldLabel = (text: string) => (
    <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem', color: 'text.primary' }}>{text}</Typography>
  );

  // ── Tap-to-explain breakdowns ─────────────────────────────────────────────
  // Every figure in the results panel can show the formula and the numbers behind it,
  // the same affordance the Buying Calculator offers.
  const dialogs: Record<string, CalcDialogInfo> = useMemo(() => {
    const bsdTiers = getBsdBreakdown(costs.shareValue)
      .filter((tier) => tier.taxable > 0)
      .map((tier): CalcDialogRow => ({ label: tier.bracket, value: fmt(tier.duty) }));

    const months = financing.effectiveTenure * 12;
    const monthlyStressRate = stressTestRate / 100 / 12;
    const pvStress = months > 0 && monthlyStressRate > 0
      ? (1 - Math.pow(1 + monthlyStressRate, -months)) / monthlyStressRate
      : months;
    const availableTdsr = Math.max(0, financing.assessableIncome * TDSR_RATIO - state.existingMonthlyDebt);

    return {
      shareValue: {
        title: 'Value of the Share Sold',
        rows: [
          { label: 'Duties are charged on the market value of the share that moves — not on the whole property, and not on the original purchase price.', value: '', note: true },
          { label: 'Current market value', value: fmt(state.marketValue) },
          { label: '× Share sold', value: `${state.exitingSharePct}%` },
          { isSeparator: true, label: '', value: '' },
          { label: `= ${fmt(state.marketValue)} × ${state.exitingSharePct}%`, value: fmt(costs.shareValue), isResult: true },
        ],
      },
      bsd: {
        title: "Buyer's Stamp Duty",
        rows: [
          { label: 'BSD is progressive, and the bands apply to the share value rather than the whole property — which is what makes a part-share transfer cheaper than a full purchase.', value: '', note: true },
          { label: 'Share value', value: fmt(costs.shareValue) },
          { isSeparator: true, label: '', value: '' },
          ...bsdTiers,
          { isSeparator: true, label: '', value: '' },
          { label: 'Total BSD', value: fmt(costs.bsd), isResult: true },
          { label: `Paid by ${buyerLabel} \u2014 the buyer takes on BSD.`, value: '', note: true },
        ],
      },
      absd: {
        title: "Additional Buyer's Stamp Duty",
        rows: costs.absd === 0
          ? [
              { label: 'No ABSD is payable. The buyer already part-owns this property and owns nothing else, so it counts as their first — and a Singapore Citizen pays 0% on a first property. This is the saving decoupling exists to capture.', value: '', note: true },
              { label: `${CITIZENSHIP_LABELS[state.remainingCitizenship]}, ${state.remainingPropertyCount} property`, value: '0%' },
              { isSeparator: true, label: '', value: '' },
              { label: 'ABSD payable', value: fmt(0), isResult: true },
            ]
          : [
              { label: "ABSD follows the buyer's citizenship and how many properties they will hold, charged on the value of the share bought.", value: '', note: true },
              { label: CITIZENSHIP_LABELS[state.remainingCitizenship], value: `${state.remainingPropertyCount} property` },
              { label: 'ABSD rate', value: `${costs.absdRatePct}%` },
              { label: 'Share value', value: fmt(costs.shareValue) },
              { isSeparator: true, label: '', value: '' },
              { label: `= ${fmt(costs.shareValue)} × ${costs.absdRatePct}%`, value: fmt(costs.absd), isResult: true },
            ],
      },
      ssd: {
        title: "Seller's Stamp Duty",
        rows: costs.ssd === 0
          ? [
              {
                label: !state.purchaseDate
                  ? 'Enter the purchase date to assess SSD. Inside the holding window the transfer is taxed like an early sale.'
                  : 'The property is outside the SSD holding window, so no SSD is payable on the transfer.',
                value: '', note: true,
              },
              ...(costs.ssdDetail ? [{ label: costs.ssdDetail.rateLabel, value: '' } as CalcDialogRow] : []),
              { label: 'SSD payable', value: fmt(0), isResult: true },
            ]
          : [
              { label: 'Inside the holding window the transfer is taxed like an early sale, on the share disposed of. Purchases from 4 Jul 2025 run a four-year schedule (16/12/8/4%); earlier ones run three years (12/8/4%).', value: '', note: true },
              { label: 'Share value', value: fmt(costs.shareValue) },
              { label: 'Holding period', value: costs.ssdDetail?.rateLabel ?? '' },
              { isSeparator: true, label: '', value: '' },
              { label: `= ${fmt(costs.shareValue)} × ${costs.ssdDetail?.rate ?? 0}%`, value: fmt(costs.ssd), isResult: true },
              { label: `Paid by ${sellerLabel} \u2014 the seller takes on SSD.`, value: '', note: true },
            ],
      },
      remainingFees: {
        title: 'Legal & Bank Fees (Acquirer)',
        rows: [
          { label: 'Buyer conveyancing', value: fmt(state.fees.buyerLegalFee) },
          { label: 'Bank Loan Penalty', value: fmt(state.fees.bankLoanPenalty) },
          { isSeparator: true, label: '', value: '' },
          { label: 'Total', value: fmt(costs.remainingOwnerFees), isResult: true },
        ],
      },
      remainingTotal: {
        title: `Total Paid by ${buyerLabel}`,
        rows: [
          { label: "Buyer's Stamp Duty", value: fmt(costs.bsd) },
          { label: `Additional Buyer's Stamp Duty (${costs.absdRatePct}%)`, value: fmt(costs.absd) },
          { label: 'Legal, bank & admin fees', value: fmt(costs.remainingOwnerFees) },
          { isSeparator: true, label: '', value: '' },
          { label: 'Subtotal', value: fmt(costs.remainingOwnerTotal), isResult: true },
        ],
      },
      exitingTotal: {
        title: `Total Paid by ${sellerLabel}`,
        rows: [
          { label: "Seller's Stamp Duty", value: fmt(costs.ssd) },
          { label: 'Seller conveyancing', value: fmt(costs.exitingOwnerFees) },
          { isSeparator: true, label: '', value: '' },
          { label: 'Subtotal', value: fmt(costs.exitingOwnerTotal), isResult: true },
        ],
      },
      totalCost: {
        title: 'Total Cost to Decouple',
        rows: [
          { label: 'What the sale costs across both sides. Weigh it against the ABSD the seller avoids on their next purchase.', value: '', note: true },
          { label: `${buyerLabel} (buyer) pays`, value: fmt(costs.remainingOwnerTotal) },
          { label: `${sellerLabel} (seller) pays`, value: fmt(costs.exitingOwnerTotal) },
          { isSeparator: true, label: '', value: '' },
          { label: 'Total', value: fmt(costs.totalCost), isResult: true },
        ],
      },
      requiredLoan: {
        title: 'New Loan Required',
        rows: [
          { label: "The whole joint loan is discharged on completion, so the new sole-name facility has to cover buying out the seller's share AND refinancing the buyer's own slice of the old loan. The buyout is an acquisition, so the bank funds it only up to the LTV — the rest is a downpayment in cash or CPF. Redeeming the buyer's own slice is a refinance and carries across in full.", value: '', note: true },
          { label: "Buy over the seller's share", value: fmt(financing.buyoutAmount) },
          { label: `× Max LTV (${Math.round(financing.ltvPct * 100)}%)`, value: fmt(financing.buyoutFinanced) },
          { label: `+ Own share of the loan redeemed (${remainingSharePct}%)`, value: fmt(financing.ownLoanShare) },
          { label: '− Own cash & CPF applied', value: fmt(financing.ownFundsApplied) },
          { isSeparator: true, label: '', value: '' },
          { label: 'New loan required', value: fmt(financing.requiredLoan), isResult: true },
        ],
      },
      ltvCap: {
        title: 'LTV Ceiling',
        rows: [
          {
            label: state.hasOtherHousingLoan
              ? 'A borrower already carrying another housing loan is capped at 45% LTV.'
              : tenureStretched
                ? 'Running the tenure past the standard cap (30 yrs, or age 65) drops the maximum LTV from 75% to 55%.'
                : 'A first housing loan within the standard tenure band is capped at 75% of the property value.',
            value: '', note: true,
          },
          { label: 'Market value (whole property)', value: fmt(state.marketValue) },
          { label: '× Max LTV', value: `${Math.round(financing.ltvPct * 100)}%` },
          { isSeparator: true, label: '', value: '' },
          { label: `= ${fmt(state.marketValue)} × ${Math.round(financing.ltvPct * 100)}%`, value: fmt(financing.ltvCap), isResult: true },
        ],
      },
      incomeCap: {
        title: 'Income Ceiling',
        rows: [
          { label: `TDSR caps total monthly debt at ${Math.round(TDSR_RATIO * 100)}% of assessable income, tested at the MAS stress floor of ${stressTestRate}% rather than the rate the bank offers.`, value: '', note: true },
          { label: 'Assessable monthly income', value: fmt(financing.assessableIncome) },
          { label: `× TDSR (${Math.round(TDSR_RATIO * 100)}%)`, value: fmt(financing.assessableIncome * TDSR_RATIO) },
          { label: '− Existing monthly debt', value: fmt(state.existingMonthlyDebt) },
          { label: '= Available TDSR / month', value: fmt(availableTdsr) },
          ...(financing.msrApplies ? [
            { isSeparator: true, label: '', value: '' },
            { label: `MSR also applies (${state.tab.toUpperCase()})`, value: '' },
            { label: `Assessable income × MSR (${Math.round(MSR_RATIO * 100)}%)`, value: fmt(financing.assessableIncome * MSR_RATIO) },
            { label: 'Binding cap = min(TDSR, MSR)', value: fmt(financing.bindingMonthlyCapacity) },
          ] as CalcDialogRow[] : []),
          { isSeparator: true, label: '', value: '' },
          { label: 'Stress test rate', value: `${stressTestRate}%` },
          { label: 'Tenure', value: `${financing.effectiveTenure} yrs = ${months} mths` },
          { label: 'PV annuity factor', value: pvStress.toFixed(4) },
          { isSeparator: true, label: '', value: '' },
          { label: `= ${fmt(financing.bindingMonthlyCapacity)} × ${pvStress.toFixed(2)}`, value: fmt(financing.incomeCap), isResult: true },
        ],
      },
      maxLoan: {
        title: 'What the Bank Will Lend',
        rows: [
          { label: 'The lower of the two ceilings binds.', value: '', note: true },
          { label: `LTV ceiling (${Math.round(financing.ltvPct * 100)}%)`, value: fmt(financing.ltvCap) },
          { label: 'Income ceiling (TDSR)', value: fmt(financing.incomeCap) },
          { isSeparator: true, label: '', value: '' },
          { label: 'Bank will lend = min of the two', value: fmt(financing.maxLoan), isResult: true },
          ...(financing.shortfall > 0 ? [
            { isSeparator: true, label: '', value: '' },
            { label: 'New loan required', value: fmt(financing.requiredLoan) },
            { label: 'Shortfall', value: fmt(financing.shortfall), isResult: true },
          ] as CalcDialogRow[] : []),
        ],
      },
      assessableIncome: {
        title: 'Assessable Monthly Income',
        rows: [
          { label: 'MAS Notice 632: base salary counts at 100% (70% if self-employed), and variable income — commission, bonus, rental — at 70%.', value: '', note: true },
          {
            label: state.isSelfEmployed
              ? `Base ${fmt(state.monthlyIncome)} × 70% (self-employed)`
              : `Base ${fmt(state.monthlyIncome)} × 100%`,
            value: fmt(state.isSelfEmployed ? state.monthlyIncome * 0.7 : state.monthlyIncome),
          },
          { label: `Other ${fmt(state.otherAnnualIncome)}/yr ÷ 12 × 70%`, value: fmt((state.otherAnnualIncome / 12) * 0.7) },
          { isSeparator: true, label: '', value: '' },
          { label: 'Assessable monthly income', value: fmt(financing.assessableIncome), isResult: true },
        ],
      },
      monthlyRepayment: {
        title: 'Monthly Repayment',
        rows: [
          { label: 'What the repayment actually costs at the rate the bank offers. TDSR is a separate test run at the stress floor, so both figures are shown — quote the first, and see what the bank assessed in the second.', value: '', note: true },
          { label: 'Loan amount', value: fmt(financing.requiredLoan) },
          { label: 'Bank rate', value: `${state.interestRate.toFixed(2)}%` },
          { label: 'Tenure', value: `${financing.effectiveTenure} yrs = ${months} mths` },
          { isSeparator: true, label: '', value: '' },
          { label: 'Monthly repayment (bank rate)', value: fmtPrecise(financing.monthlyAtBankRate), isResult: true },
          { isSeparator: true, label: '', value: '' },
          { label: `At the ${stressTestRate}% stress floor`, value: fmtPrecise(financing.monthlyAtStressRate) },
          { label: 'Monthly repayment capacity', value: fmt(financing.bindingMonthlyCapacity) },
        ],
      },
      tenureCap: {
        title: 'Loan Tenure Cap',
        rows: [
          { label: 'Tenure is capped by age. Staying inside the standard band keeps the full 75% LTV; stretching beyond it is allowed but drops the ceiling to 55%.', value: '', note: true },
          { label: 'Age', value: `${state.age} yrs` },
          { isSeparator: true, label: '', value: '' },
          { label: 'Standard: min(30 yrs, 65 − age)', value: `${standardTenureCap} yrs` },
          { label: 'Extended: min(35 yrs, 75 − age)', value: `${getTenureCap(state.age, true)} yrs` },
          ...(state.tab === 'hdb' ? [{ label: 'HDB hard cap', value: '25 yrs' } as CalcDialogRow] : []),
          { isSeparator: true, label: '', value: '' },
          { label: 'Cap used here', value: `${maxTenureCap} yrs`, isResult: true },
          { label: 'Tenure selected', value: `${financing.effectiveTenure} yrs` },
        ],
      },
      cashRequired: {
        title: 'Cash Needed on Completion',
        rows: [
          { label: 'What the buyer must produce on the day: the duties and fees they bear, the part of the buyout the bank will not fund, plus anything else it will not lend.', value: '', note: true },
          { label: 'Duties and fees the buyer bears', value: fmt(costs.remainingOwnerTotal) },
          { label: `Buyout downpayment (${100 - Math.round(financing.ltvPct * 100)}% of the share) not covered by own funds`, value: fmt(financing.downpaymentShortfall) },
          { label: 'Loan shortfall', value: fmt(financing.shortfall) },
          { isSeparator: true, label: '', value: '' },
          { label: 'Cash needed', value: fmt(financing.cashRequired), isResult: true },
        ],
      },
      netCash: {
        title: `${sellerLabel} — Net Cash`,
        rows: [
          { label: "The CPF refund comes off here because it returns to the owner's own CPF OA rather than reaching their pocket. It is still theirs — see CPF OA restored.", value: '', note: true },
          { label: 'Consideration for their share', value: fmt(exitPosition.consideration) },
          { label: '− CPF refund to their own OA', value: fmt(exitPosition.cpfRefund) },
          { label: `− Their share of the loan redeemed (${state.exitingSharePct}%)`, value: fmt(exitPosition.loanShareRedeemed) },
          { label: "− Seller's Stamp Duty", value: fmt(exitPosition.ssd) },
          { label: '− Seller conveyancing', value: fmt(exitPosition.fees) },
          { isSeparator: true, label: '', value: '' },
          { label: 'Net cash in hand', value: fmt(exitPosition.netCash), isResult: true },
          ...(exitPosition.netCash < 0 ? [
            { label: 'Negative: the loan share and CPF refund together exceed the share value, so the owner has to top up on completion rather than walk away with anything.', value: '', note: true },
          ] as CalcDialogRow[] : []),
        ],
      },
      cpfRestored: {
        title: 'CPF OA Restored',
        rows: [
          { label: "The CPF drawn for this property, plus the accrued interest it would have earned, returns to the seller's Ordinary Account on completion.", value: '', note: true },
          { label: 'CPF principal + accrued interest', value: fmt(exitPosition.cpfRefund), isResult: true },
          { label: 'Available again for their next purchase, but locked in CPF — it cannot be taken as cash.', value: '', note: true },
        ],
      },
      totalReceived: {
        title: `${sellerLabel} — Total Across Both Pockets`,
        rows: [
          { label: 'Net cash in hand', value: fmt(exitPosition.netCash) },
          { label: 'CPF OA restored', value: fmt(exitPosition.cpfRefund) },
          { isSeparator: true, label: '', value: '' },
          { label: 'Total', value: fmt(exitPosition.totalReceived), isResult: true },
        ],
      },
    };
  }, [
    state, costs, financing, exitPosition, stressTestRate,
    sellerLabel, buyerLabel, remainingSharePct, standardTenureCap, maxTenureCap, tenureStretched,
  ]);

  const openExplain = useCallback((key: string) => {
    setCalcDialog(dialogs[key] ?? null);
  }, [dialogs]);

  /** One line in a results card. Pass `explainKey` to make it tappable. */
  const renderResultRow = (
    label: string,
    value: string,
    opts?: { explainKey?: string; bold?: boolean; indent?: boolean; color?: string; note?: string },
  ) => (
    <Box sx={{ mb: opts?.note ? 0.25 : 0 }}>
      <Box
        onClick={opts?.explainKey ? () => openExplain(opts.explainKey!) : undefined}
        sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          gap: 2, py: 0.6, pl: opts?.indent ? 2 : 0, px: 0.5, mx: -0.5,
          borderRadius: 1,
          cursor: opts?.explainKey ? 'pointer' : 'default',
          '&:hover': opts?.explainKey
            ? { bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' }
            : undefined,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: opts?.bold ? 700 : 400, color: opts?.color ?? 'text.primary' }}>
            {label}
          </Typography>
          {opts?.explainKey && <InfoOutlinedIcon sx={{ fontSize: 15, color: highlightColor, flexShrink: 0 }} />}
        </Box>
        <Typography variant="body2" sx={{
          fontWeight: opts?.bold ? 800 : 600, color: opts?.color ?? 'text.primary',
          whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
        }}>
          {value}
        </Typography>
      </Box>
      {opts?.note && (
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', pl: opts?.indent ? 2 : 0, mb: 0.5 }}>
          {opts.note}
        </Typography>
      )}
    </Box>
  );

  // ── Left panel ────────────────────────────────────────────────────────────
  const leftPanel = (
    <Box>
      {/* ── PROPERTY ── */}
      <Card data-tutorial="property-section" variant="outlined" sx={sectionCardSx}>
        <SectionCardHeader title="Property" step={1} color={tabColor} isDark={isDark} cardBorder={cardBorder} />
        <Box sx={sectionBodySx}>
          <MoneyField
            label="Current Market Value"
            value={state.marketValue}
            onChange={(v) => update('marketValue', v)}
            disabled={blocked}
            helperText="The bank orders a fresh valuation — duties are charged on this, not the original price."
          />
          <MoneyField
            label="Outstanding Loan (whole property)"
            value={state.outstandingLoan}
            onChange={(v) => update('outstandingLoan', v)}
            disabled={blocked}
            helperText="Discharged in full on completion and replaced by a new loan in one name."
          />
          <TextField
            size="small" fullWidth type="date" disabled={blocked}
            label="Transfer Date"
            InputLabelProps={{ shrink: true }}
            value={state.transferDate}
            onChange={(e) => update('transferDate', e.target.value)}
            helperText="Completion — typically 8–12 weeks out"
          />
        </Box>
      </Card>

      {/* ── OWNERSHIP ── */}
      <Card data-tutorial="ownership-section" variant="outlined" sx={sectionCardSx}>
        <SectionCardHeader title="Seller" step={2} color={tabColor} isDark={isDark} cardBorder={cardBorder} />
        <Box sx={sectionBodySx}>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: roleMembers.length > 0 ? 1 : 2 }}>
            The owner selling their share. Their name comes off the title, which is what frees
            them to buy their next property at first-property ABSD rates. The CPF figure is the
            amount they drew for this property <strong>plus accrued interest</strong>.
          </Typography>
          {roleMembers.length > 0 && (
            <Button
              size="small"
              startIcon={<SwapHorizIcon sx={{ fontSize: 16 }} />}
              onClick={() => setRoleDialogOpen(true)}
              disabled={blocked}
              sx={{ textTransform: 'none', mb: 1.5, p: 0, minWidth: 0, color: highlightColor }}
            >
              Change who is buying and selling
            </Button>
          )}

          {/* The seller — the owner whose share is being bought over. Same card shell and
              same label-left / field-right rows as the Buyer card in Step 3, so the two
              people on the deal read as a matching pair. */}
          <Card variant="outlined" sx={{
            p: 2, mb: 2, bgcolor: cardBg, borderColor: cardBorder,
            borderTop: `2px solid ${highlightColor}`, overflow: 'visible',
          }}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ color: tabColor }}>
              {sellerHeading}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
              Sells their share and comes off the title.
            </Typography>

            {/* Name */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
              <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem', flexShrink: 0 }}>Name</Typography>
              <TextField
                size="small"
                disabled={blocked}
                placeholder="e.g. Adam Tan"
                value={state.exitingName}
                onChange={(e) => update('exitingName', e.target.value)}
                sx={{ width: 180, '& .MuiInputBase-input': { fontSize: '0.85rem', textAlign: 'right', py: 0.9 } }}
              />
            </Box>

            <Divider sx={{ mb: 1, opacity: 0.5 }} />

            {/* CPF already sunk into this property — refunded to them on completion */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem' }}>CPF used + accrued interest</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>Refunded to their own OA on completion — not payable as cash</Typography>
              </Box>
              <Box sx={{ width: 180, flexShrink: 0 }}>
                <CurrencyTextField value={state.exitingCpf} onChange={(v) => update('exitingCpf', v)} highlightColor={highlightColor} />
              </Box>
            </Box>

            <Divider sx={{ mb: 1.5, mt: 1.5, opacity: 0.5 }} />

            {/* Share sold */}
            <Box sx={{ mb: 2 }}>
              <EditableSliderValueRow
                label={borrowerFieldLabel('Share sold')}
                value={state.exitingSharePct}
                min={1}
                max={99}
                step={1}
                onCommit={(v) => update('exitingSharePct', v)}
                formatDisplay={(v) => `${v}%`}
                valueColor={highlightColor}
                disabled={blocked}
              />
              <Box sx={{ pl: 1.5, pr: 3, overflow: 'hidden' }}>
                <IntentGuardedSlider
                  isMobile={isMobile}
                  value={state.exitingSharePct}
                  min={1}
                  max={99}
                  step={1}
                  disabled={blocked}
                  onChange={(_, v) => update('exitingSharePct', v as number)}
                  sx={{ color: highlightColor, height: 6 }}
                />
              </Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Buyer already holds {remainingSharePct}%
              </Typography>
            </Box>

            <Divider sx={{ mb: 2, opacity: 0.5 }} />

            {/* Citizenship — the same radio row the Buyer card uses */}
            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem', color: 'text.primary', mb: 0.75 }}>Citizenship</Typography>
              <RadioGroup
                row
                value={state.exitingCitizenship}
                onChange={(e) => update('exitingCitizenship', e.target.value as CitizenshipType)}
              >
                {(Object.keys(CITIZENSHIP_LABELS) as CitizenshipType[]).map((key) => (
                  <FormControlLabel
                    key={key} value={key} disabled={blocked}
                    control={<Radio size="small" sx={{ color: highlightColor, '&.Mui-checked': { color: highlightColor } }} />}
                    label={<Typography variant="body2">{CITIZENSHIP_LABELS[key]}</Typography>}
                  />
                ))}
              </RadioGroup>
            </Box>
          </Card>

        </Box>
      </Card>

      {/* ── BUYER (BORROWER) ── */}
      {/* Field order mirrors the Buying Calculator's borrower card: name, age, income,
          other income, self-employed, debt, own funds, citizenship, property count —
          then the loan terms at the foot of the section. */}
      <Card data-tutorial="financing-section" variant="outlined" sx={sectionCardSx}>
        <SectionCardHeader title="Buyer" step={3} color={tabColor} isDark={isDark} cardBorder={cardBorder} />
        <Box sx={sectionBodySx}>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
            The new loan sits in the buyer&rsquo;s name only, so they must clear TDSR alone
            &mdash; stressed at {stressTestRate}%, not the contracted rate.
          </Typography>

          <Card variant="outlined" sx={{
            p: 2, mb: 2, bgcolor: cardBg, borderColor: cardBorder,
            borderTop: `2px solid ${highlightColor}`, overflow: 'visible',
          }}>
            {/* Name */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
              <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem', flexShrink: 0 }}>Name</Typography>
              <TextField
                size="small"
                disabled={blocked}
                placeholder="e.g. Adam Tan"
                value={state.remainingName}
                onChange={(e) => update('remainingName', e.target.value)}
                sx={{ width: 180, '& .MuiInputBase-input': { fontSize: '0.85rem', textAlign: 'right', py: 0.9 } }}
              />
            </Box>

            <Divider sx={{ mb: 1, opacity: 0.5 }} />

            {/* CPF already sunk into this property — sits under the name on both cards.
                Not to be confused with the CPF OA Balance further down: that one is money
                available to shrink the new loan, this one is already in the property. */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem' }}>CPF used + accrued interest</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>Already in this property — stays put, shown for context</Typography>
              </Box>
              <Box sx={{ width: 180, flexShrink: 0 }}>
                <CurrencyTextField value={state.remainingCpf} onChange={(v) => update('remainingCpf', v)} highlightColor={highlightColor} />
              </Box>
            </Box>

            <Divider sx={{ mb: 1.5, mt: 1.5, opacity: 0.5 }} />

            {/* Age */}
            <Box sx={{ mb: 2 }}>
              <EditableSliderValueRow
                label={borrowerFieldLabel('Age')}
                value={state.age}
                min={21}
                max={70}
                step={1}
                onCommit={(v) => update('age', v)}
                formatDisplay={(v) => `${v} yrs`}
                valueColor={highlightColor}
                disabled={blocked}
              />
              <Box sx={{ pl: 1.5, pr: 3 }}>
                <IntentGuardedSlider
                  isMobile={isMobile}
                  value={state.age}
                  min={21}
                  max={70}
                  step={1}
                  disabled={blocked}
                  onChange={(_, v) => update('age', v as number)}
                  sx={{ color: highlightColor, height: 6 }}
                />
              </Box>
            </Box>

            <Divider sx={{ mb: 1.5, opacity: 0.5 }} />

            {/* Monthly Income */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
              <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem', flexShrink: 0 }}>Monthly Income</Typography>
              <Box sx={{ width: 180 }}>
                <CurrencyTextField value={state.monthlyIncome} onChange={(v) => update('monthlyIncome', v)} highlightColor={highlightColor} />
              </Box>
            </Box>

            <Divider sx={{ mb: 1, opacity: 0.5 }} />

            {/* Other Annual Income */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem' }}>Other Annual Income</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                  30% haircut &mdash; recognised: {fmt(Math.round(state.otherAnnualIncome * 0.7 / 12))}/mo
                </Typography>
              </Box>
              <Box sx={{ width: 180, flexShrink: 0 }}>
                <CurrencyTextField value={state.otherAnnualIncome} onChange={(v) => update('otherAnnualIncome', v)} highlightColor={highlightColor} />
              </Box>
            </Box>

            <Divider sx={{ mb: 1, opacity: 0.5 }} />

            {/* Self-employed */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Checkbox
                    size="small" sx={{ p: 0.5 }} disabled={blocked}
                    checked={state.isSelfEmployed}
                    onChange={(e) => update('isSelfEmployed', e.target.checked)}
                  />
                }
                label={<Typography variant="body2" sx={{ fontSize: '0.82rem' }}>Self-employed</Typography>}
              />
              <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'right', flexShrink: 0 }}>
                Recognised: {fmt(financing.assessableIncome)}
              </Typography>
            </Box>

            <Divider sx={{ mb: 1, opacity: 0.5 }} />

            {/* Existing Monthly Debt */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
              <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem', flexShrink: 0 }}>Existing Monthly Debt</Typography>
              <Box sx={{ width: 180 }}>
                <CurrencyTextField value={state.existingMonthlyDebt} onChange={(v) => update('existingMonthlyDebt', v)} highlightColor={highlightColor} />
              </Box>
            </Box>

            <Divider sx={{ mb: 1, mt: 1, opacity: 0.5 }} />

            {/* Own funds — applied against the new loan rather than a purchase price */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem' }}>Cash / Saving</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>Reduces the new loan</Typography>
              </Box>
              <Box sx={{ width: 180, flexShrink: 0 }}>
                <CurrencyTextField value={state.cashAvailable} onChange={(v) => update('cashAvailable', v)} highlightColor={highlightColor} />
              </Box>
            </Box>

            <Divider sx={{ mb: 1, opacity: 0.5 }} />

            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem' }}>CPF OA Balance</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>Reduces the new loan</Typography>
              </Box>
              <Box sx={{ width: 180, flexShrink: 0 }}>
                <CurrencyTextField value={state.cpfAvailable} onChange={(v) => update('cpfAvailable', v)} highlightColor={highlightColor} />
              </Box>
            </Box>

            <Divider sx={{ mb: 2, mt: 2, opacity: 0.5 }} />

            {/* Citizenship & which property — these drive ABSD on the share acquired */}
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem', color: 'text.primary', mb: 0.75 }}>Citizenship</Typography>
              <RadioGroup
                row
                value={state.remainingCitizenship}
                onChange={(e) => update('remainingCitizenship', e.target.value as CitizenshipType)}
              >
                {(Object.keys(CITIZENSHIP_LABELS) as CitizenshipType[]).map((key) => (
                  <FormControlLabel
                    key={key} value={key} disabled={blocked}
                    control={<Radio size="small" sx={{ color: highlightColor, '&.Mui-checked': { color: highlightColor } }} />}
                    label={<Typography variant="body2">{CITIZENSHIP_LABELS[key]}</Typography>}
                  />
                ))}
              </RadioGroup>
            </Box>

            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem', color: 'text.primary', mb: 0.75 }}>Which Property</Typography>
              <RadioGroup
                row
                value={state.remainingPropertyCount}
                onChange={(e) => update('remainingPropertyCount', e.target.value as PropertyCount)}
              >
                {PROPERTY_COUNT_OPTIONS.map((opt) => (
                  <FormControlLabel
                    key={opt.value} value={opt.value} disabled={blocked}
                    control={<Radio size="small" sx={{ color: highlightColor, '&.Mui-checked': { color: highlightColor } }} />}
                    label={<Typography variant="body2">{opt.label}</Typography>}
                  />
                ))}
              </RadioGroup>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                Counting this one. 1st Property means no ABSD on the share acquired.
              </Typography>
            </Box>
          </Card>

          {/* ── Loan terms ── */}
          <Typography variant="body2" fontWeight={800} sx={{ fontSize: '0.82rem', mb: 1.5, color: tabColor }}>
            Loan Terms
          </Typography>

          {/* Loan tenure */}
          <Box sx={{ mb: 2 }}>
            <EditableSliderValueRow
              label={borrowerFieldLabel('Loan Tenure')}
              value={state.tenure}
              min={1}
              max={Math.max(1, maxTenureCap)}
              step={1}
              onCommit={(v) => requestTenureChange(v)}
              formatDisplay={(v) => `${v} yrs`}
              valueColor={highlightColor}
              disabled={blocked}
            />
            <Box sx={{ pl: 1.5, pr: 3, overflow: 'hidden' }}>
              <IntentGuardedSlider
                isMobile={isMobile}
                value={state.tenure}
                min={1}
                max={Math.max(1, maxTenureCap)}
                step={1}
                disabled={blocked}
                onChange={(_, v) => requestTenureChange(v as number)}
                sx={{ color: highlightColor, height: 6 }}
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>1 yr</Typography>
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>Cap: {maxTenureCap} yrs</Typography>
            </Box>
            {tenureStretched && (
              <Typography variant="caption" sx={{ display: 'block', color: 'warning.main', mt: 0.5 }}>
                Past the {standardTenureCap}-yr standard cap &mdash; max LTV drops to 55%.
              </Typography>
            )}
          </Box>

          {/* Bank rate */}
          <Box sx={{ mb: 1.5 }}>
            <EditableSliderValueRow
              label={borrowerFieldLabel('Bank Rate')}
              value={state.interestRate}
              min={0.5}
              max={6.0}
              step={0.01}
              onCommit={(v) => update('interestRate', v)}
              formatDisplay={(v) => `${v.toFixed(2)}%`}
              valueColor={highlightColor}
              commitMax={15}
              disabled={blocked}
            />
            <Box sx={{ pl: 1.5, pr: 3, overflow: 'hidden' }}>
              <IntentGuardedSlider
                isMobile={isMobile}
                value={state.interestRate}
                min={0.5}
                max={6.0}
                step={0.05}
                disabled={blocked}
                onChange={(_, v) => update('interestRate', v as number)}
                sx={{ color: highlightColor, height: 6 }}
              />
            </Box>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Drives the monthly repayment. TDSR is still assessed at {stressTestRate}%.
            </Typography>
          </Box>

          <FormControlLabel
            sx={{ m: 0 }}
            control={
              <Checkbox
                size="small" sx={{ p: 0.5 }} disabled={blocked}
                checked={state.hasOtherHousingLoan}
                onChange={(e) => update('hasOtherHousingLoan', e.target.checked)}
              />
            }
            label={<Typography variant="body2" sx={{ fontSize: '0.82rem' }}>Already has another housing loan (LTV drops to 45%)</Typography>}
          />
        </Box>
      </Card>

      {/* ── SELLER STAMP DUTY ── */}
      {/* Same control as the Selling Calculator's: SSD only bites inside the holding
          window, so it stays off until the agent switches it on and dates the purchase. */}
      <Card data-tutorial="ssd-section" variant="outlined" sx={sectionCardSx}>
        <SectionCardHeader title="Seller Stamp Duty" step={4} color={tabColor} isDark={isDark} cardBorder={cardBorder} />
        <Box sx={sectionBodySx}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" fontWeight={600}>Apply Seller Stamp Duty</Typography>
                <IconButton
                  size="small"
                  aria-label="View Seller Stamp Duty purchase-date rates"
                  aria-controls={ssdInfoAnchor ? 'decoupling-ssd-info' : undefined}
                  aria-haspopup="dialog"
                  aria-expanded={Boolean(ssdInfoAnchor)}
                  onClick={(event) => setSsdInfoAnchor(event.currentTarget)}
                  sx={{ width: 36, height: 36, p: 0.5, color: 'text.secondary', touchAction: 'manipulation' }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 20 }} />
                </IconButton>
                <Popover
                  id="decoupling-ssd-info"
                  open={Boolean(ssdInfoAnchor)}
                  anchorEl={ssdInfoAnchor}
                  onClose={() => setSsdInfoAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  slotProps={{ paper: { sx: { maxWidth: 'min(360px, calc(100vw - 32px))' } } }}
                >
                  <Box sx={{ p: 2 }}>
                    <Typography variant="caption" component="div" fontWeight={700} sx={{ mb: 0.5 }}>
                      Purchased on or after 4 Jul 2025
                    </Typography>
                    <Typography variant="caption" component="div">
                      Under 1 year: 16% · 1–2 years: 12% · 2–3 years: 8% · 3–4 years: 4%. No SSD from 4 years.
                    </Typography>
                    <Typography variant="caption" component="div" fontWeight={700} sx={{ mt: 1, mb: 0.5 }}>
                      Purchased 11 Mar 2017–3 Jul 2025
                    </Typography>
                    <Typography variant="caption" component="div">
                      Under 1 year: 12% · 1–2 years: 8% · 2–3 years: 4%. No SSD from 3 years.
                    </Typography>
                    <Link
                      href={IRAS_SSD_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      color="inherit"
                      underline="always"
                      variant="caption"
                      sx={{ display: 'inline-block', mt: 1, fontWeight: 700 }}
                    >
                      View IRAS SSD rates
                    </Link>
                  </Box>
                </Popover>
              </Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Payable if sold within 3 or 4 years, based on purchase date
              </Typography>
            </Box>
            <Switch
              checked={state.ssdApplies}
              onChange={(e) => update('ssdApplies', e.target.checked)}
              size="small"
              disabled={blocked}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: tabColor },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: tabColor },
              }}
            />
          </Box>

          <Collapse in={state.ssdApplies} timeout="auto" unmountOnExit>
            <Box sx={{ mt: 2 }}>
              <TextField
                size="small" fullWidth type="date" disabled={blocked}
                label="Purchase Date"
                InputLabelProps={{ shrink: true }}
                value={state.purchaseDate}
                onChange={(e) => update('purchaseDate', e.target.value)}
                helperText="When this property was acquired — key collection for an EC. With the transfer date, it sets the holding period."
                sx={{ mb: 2 }}
              />
              <TextField
                size="small"
                fullWidth
                disabled
                label="Seller Stamp Duty (on the share sold)"
                InputLabelProps={{ shrink: true }}
                value={costs.ssd.toLocaleString('en-SG')}
                InputProps={{ startAdornment: <InputAdornment position="start">S$</InputAdornment> }}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'action.disabledBackground' } }}
                helperText={state.purchaseDate
                  ? (costs.ssdDetail
                      ? costs.ssdDetail.rateLabel.replace(' (', ' — ').replace(')', '')
                      : 'Enter a valid purchase date to calculate SSD')
                  : 'Enter the purchase date to calculate SSD'}
              />
            </Box>
          </Collapse>
        </Box>
      </Card>

      {/* ── FEES ── */}
      <Card variant="outlined" sx={sectionCardSx}>
        <SectionCardHeader title="Legal & Bank Fees" step={5} color={tabColor} isDark={isDark} cardBorder={cardBorder} />
        <Box sx={sectionBodySx}>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
            The two sides must be represented by different law firms, so there are two sets
            of conveyancing. Overwrite the figures with the actual quotes.
          </Typography>
          <MoneyField label="Buyer conveyancing" value={state.fees.buyerLegalFee} onChange={(v) => updateFee('buyerLegalFee', v)} disabled={blocked} helperText={FEE_GUIDANCE.buyerLegalFee} />
          <MoneyField label="Seller conveyancing" value={state.fees.sellerLegalFee} onChange={(v) => updateFee('sellerLegalFee', v)} disabled={blocked} helperText={FEE_GUIDANCE.sellerLegalFee} />
          <MoneyField label="Bank Loan Penalty" value={state.fees.bankLoanPenalty} onChange={(v) => updateFee('bankLoanPenalty', v)} disabled={blocked} helperText={FEE_GUIDANCE.bankLoanPenalty} />
        </Box>
      </Card>
    </Box>
  );

  // ── Right panel ───────────────────────────────────────────────────────────
  const rightPanel = (
    <Box data-tutorial="results">
      {/* Guards */}
      {eligibility.guards.map((guard, idx) => (
        <Alert
          key={idx}
          severity={guard.level === 'blocked' ? 'error' : guard.level === 'warning' ? 'warning' : 'info'}
          sx={{ mb: 2, borderRadius: 2 }}
        >
          <AlertTitle sx={{ fontWeight: 800 }}>{guard.title}</AlertTitle>
          {guard.body.map((line, i) => (
            <Typography key={i} variant="body2" sx={{ mb: i === guard.body.length - 1 ? 0 : 1 }}>
              {line}
            </Typography>
          ))}
        </Alert>
      ))}

      {blocked ? null : (
        <>
          {/* ── TRANSFER COST ── */}
          <Card variant="outlined" sx={sectionCardSx}>
            <SectionCardHeader title="Cost to Decouple" color={tabColor} isDark={isDark} cardBorder={cardBorder} />
            <Box sx={sectionBodySx}>
              {renderResultRow(
                `Share sold (${state.exitingSharePct}% of ${fmt(state.marketValue)})`,
                fmt(costs.shareValue),
                { bold: true, explainKey: 'shareValue' },
              )}
              <Divider sx={{ my: 1.5 }} />

              <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Paid by {buyerLabel} (Buyer)
              </Typography>
              {renderResultRow("Buyer's Stamp Duty", fmt(costs.bsd), { indent: true, explainKey: 'bsd' })}
              {renderResultRow(
                `Additional Buyer's Stamp Duty (${costs.absdRatePct}%)`,
                fmt(costs.absd),
                {
                  indent: true,
                  explainKey: 'absd',
                  color: costs.absd > 0 ? 'error.main' : undefined,
                  note: costs.absd === 0 ? 'No ABSD — this becomes their only property.' : undefined,
                },
              )}
              {renderResultRow('Legal, bank & admin fees', fmt(costs.remainingOwnerFees), { indent: true, explainKey: 'remainingFees' })}
              {renderResultRow('Subtotal', fmt(costs.remainingOwnerTotal), { bold: true, indent: true, explainKey: 'remainingTotal' })}

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Paid by {sellerLabel} (Seller)
              </Typography>
              {renderResultRow(
                `Seller's Stamp Duty${costs.ssdDetail ? ` (${costs.ssdDetail.rateLabel})` : ''}`,
                fmt(costs.ssd),
                {
                  indent: true,
                  explainKey: 'ssd',
                  color: costs.ssd > 0 ? 'error.main' : undefined,
                  note: !state.purchaseDate ? 'Enter a purchase date to assess SSD.' : undefined,
                },
              )}
              {renderResultRow('Seller conveyancing', fmt(costs.exitingOwnerFees), { indent: true })}
              {renderResultRow('Subtotal', fmt(costs.exitingOwnerTotal), { bold: true, indent: true, explainKey: 'exitingTotal' })}

              <Divider sx={{ my: 1.5 }} />
              {renderResultRow('Total cost to decouple', fmt(costs.totalCost), { bold: true, color: tabColor, explainKey: 'totalCost' })}
            </Box>
          </Card>

          {/* ── REMAINING OWNER FINANCING ── */}
          <Card variant="outlined" sx={sectionCardSx}>
            <SectionCardHeader title={buyerHeading} note="Sole-name loan" color={tabColor} isDark={isDark} cardBorder={cardBorder} />
            <Box sx={sectionBodySx}>
              <Alert
                severity={!loanAssessable ? 'info' : financing.passes ? 'success' : 'error'}
                icon={!loanAssessable ? <InfoOutlinedIcon /> : financing.passes ? <CheckCircleOutlineIcon /> : <ErrorOutlineIcon />}
                sx={{ mb: 2, borderRadius: 2 }}
              >
                <AlertTitle sx={{ fontWeight: 800, mb: 0.5 }}>
                  {!loanAssessable
                    ? 'Nothing to assess yet'
                    : financing.passes
                      ? 'Can carry the property alone'
                      : `Shortfall of ${fmt(financing.shortfall)}`}
                </AlertTitle>
                <Typography variant="body2">
                  {!loanAssessable
                    ? "Enter the market value and the buyer's income to see whether the new loan clears."
                    : financing.passes
                      ? `The bank will lend up to ${fmt(financing.maxLoan)} against ${fmt(financing.requiredLoan)} needed.`
                      : `${fmt(financing.requiredLoan)} is needed but the bank will only lend ${fmt(financing.maxLoan)}. Close the gap with more cash or CPF, a longer tenure, or pledged funds.`}
                </Typography>
              </Alert>

              {/* The headline figure first. Its make-up — the buyout at LTV, the buyer's own
                  slice of the old loan, any funds applied — rides along as one caption and
                  is laid out in full in the explain dialog. */}
              {renderResultRow('New loan required', fmt(financing.requiredLoan), {
                bold: true,
                color: tabColor,
                explainKey: 'requiredLoan',
                note: loanMakeup,
              })}

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                What the Bank Will Lend
              </Typography>
              {renderResultRow('Assessable monthly income', fmt(financing.assessableIncome), { indent: true, explainKey: 'assessableIncome' })}
              {renderResultRow('Monthly repayment capacity', fmt(financing.bindingMonthlyCapacity), { indent: true, explainKey: 'incomeCap' })}
              {renderResultRow(`LTV ceiling (${Math.round(financing.ltvPct * 100)}% of market value)`, fmt(financing.ltvCap), { indent: true, explainKey: 'ltvCap' })}
              {renderResultRow(
                `Income ceiling (${financing.msrBinds ? 'MSR 30%' : 'TDSR 55%'} over ${financing.effectiveTenure} yrs @ ${stressTestRate}%)`,
                fmt(financing.incomeCap),
                {
                  indent: true,
                  explainKey: 'incomeCap',
                  note: financing.msrApplies && !financing.msrBinds ? 'TDSR binds ahead of MSR here.' : undefined,
                },
              )}
              {renderResultRow('Bank will lend', fmt(financing.maxLoan), { bold: true, indent: true, explainKey: 'maxLoan' })}

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                What They Pay
              </Typography>
              {renderResultRow(
                `Monthly repayment @ ${state.interestRate.toFixed(2)}%`,
                fmtPrecise(financing.monthlyAtBankRate),
                {
                  bold: true,
                  indent: true,
                  explainKey: 'monthlyRepayment',
                  note: `${fmtPrecise(financing.monthlyAtStressRate)} at the ${stressTestRate}% stress floor, which is what TDSR is tested against.`,
                },
              )}
              {renderResultRow('Loan tenure', `${financing.effectiveTenure} yrs`, { indent: true, explainKey: 'tenureCap' })}

              <Divider sx={{ my: 1.5 }} />
              {renderResultRow('Cash needed on completion', fmt(financing.cashRequired), {
                bold: true,
                color: tabColor,
                explainKey: 'cashRequired',
                note: financing.downpaymentShortfall > 0
                  ? `Duties and fees the buyer bears, the ${fmt(financing.downpaymentShortfall)} buyout downpayment the bank won't fund, plus any loan shortfall.`
                  : 'Duties and fees the buyer bears, plus any loan shortfall.',
              })}
            </Box>
          </Card>

          {/* ── EXITING OWNER ── */}
          <Card variant="outlined" sx={sectionCardSx}>
            <SectionCardHeader title={sellerHeading} note="What they walk away with" color={tabColor} isDark={isDark} cardBorder={cardBorder} />
            <Box sx={sectionBodySx}>
              {renderResultRow('Consideration for their share', fmt(exitPosition.consideration), { explainKey: 'netCash' })}
              {renderResultRow('− CPF refund to their own OA', fmt(exitPosition.cpfRefund), { indent: true, explainKey: 'cpfRestored' })}
              {renderResultRow(`− Their share of the loan redeemed (${state.exitingSharePct}%)`, fmt(exitPosition.loanShareRedeemed), { indent: true })}
              {exitPosition.ssd > 0 && renderResultRow("− Seller's Stamp Duty", fmt(exitPosition.ssd), { indent: true, color: 'error.main', explainKey: 'ssd' })}
              {renderResultRow('− Seller conveyancing', fmt(exitPosition.fees), { indent: true })}
              <Divider sx={{ my: 1.5 }} />
              {renderResultRow('Net cash in hand', fmt(exitPosition.netCash), {
                bold: true,
                explainKey: 'netCash',
                color: exitPosition.netCash < 0 ? 'error.main' : tabColor,
                note: exitPosition.netCash < 0
                  ? 'Negative — the loan share and CPF refund exceed the share value, so they must top up on completion.'
                  : undefined,
              })}
              {renderResultRow('CPF OA restored', fmt(exitPosition.cpfRefund), {
                bold: true,
                explainKey: 'cpfRestored',
                note: 'Available again for their next purchase, but not payable as cash.',
              })}
              <Divider sx={{ my: 1.5 }} />
              {renderResultRow('Total across both pockets', fmt(exitPosition.totalReceived), { bold: true, explainKey: 'totalReceived' })}

              <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                <Typography variant="body2">
                  Move to the <strong>Buying Calculator</strong> and these funds travel with
                  you &mdash; with {sellerLabel} as the only borrower, so the
                  next purchase is priced at first-property ABSD rates.
                </Typography>
              </Alert>
            </Box>
          </Card>
        </>
      )}
    </Box>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, md: 3 }, py: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={700}>
          Decoupling Calculator
        </Typography>
        <TutorialLaunchButton controller={tutorial} label="the Decoupling Calculator" />
      </Box>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        One owner sells their share to the other &mdash; what the sale costs each side,
        and whether the buyer can carry the property alone.
      </Typography>

      {/* Load Example / History + Actions */}
      <Box data-tutorial="load-example">
        <HistoryToolbar
          analyticsCalculator="decoupling_calculator"
          onHistoryLoad={() => { /* state already restored via the registered setter */ }}
          onSaveToProfile={saveInputsToProfile}
          onBuildNewProfiles={buildNewProfilesFromInputs}
          onBeforeProfileCreate={beginProfileSaveBack}
          onAfterProfileCreate={endProfileSaveBack}
          extraButtons={
            <>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<RestartAltIcon />}
                onClick={() => setClearDialogOpen(true)}
              >
                Clear
              </Button>
              <ExportPdfButton onExport={handleExportPdf} disabled={blocked} />
            </>
          }
        />
      </Box>

      {/* Display controls. There is no property-type tab bar: an HDB flat cannot be
          decoupled at all, and an EC only once it has cleared MOP — by which point it is
          private property and priced as one. So the page is private-only. */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <TextSizeControl />
          {!isMobile && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={splitScroll ? 'split' : 'single'}
              onChange={(_, v) => { if (v !== null) setSplitScroll(v === 'split'); }}
              sx={{
                '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', px: 1.25, py: 0.5 },
                '& .Mui-selected': { color: `${tabColor} !important`, bgcolor: isDark ? 'rgba(255,255,255,0.10) !important' : 'rgba(30,58,95,0.08) !important' },
              }}
            >
              <Tooltip title="Page scrolls as one column">
                <ToggleButton value="single">1 Scroll</ToggleButton>
              </Tooltip>
              <Tooltip title="Inputs and results scroll independently">
                <ToggleButton value="split">2 Scroll</ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>
          )}
        </Box>
      </Box>

      {/* Clear confirmation */}
      <Dialog open={clearDialogOpen} onClose={() => setClearDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem' }}>Clear Decoupling Calculator?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            This will reset all Decoupling Calculator values to their original defaults.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your saved history entries will not be deleted.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1 }}>
          <Button onClick={() => setClearDialogOpen(false)} size="small">Cancel</Button>
          <Button onClick={clearCalculator} size="small" variant="contained" color="error">
            Clear &amp; Reset
          </Button>
        </DialogActions>
      </Dialog>

      {/* Role assignment — a decoupling has two sides, and a saved profile says nothing
          about which one a person is on. Rather than guess, ask once per selection. */}
      <Dialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem' }}>
          {roleMembers.length === 1
            ? `Which side is ${memberLabel(roleMembers[0], 0)} on?`
            : 'Who is the seller?'}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            The seller has their name taken off the title, which frees them to buy their next
            property at first-property ABSD rates. The buyer takes over the whole property and
            the new loan in their sole name.
          </Typography>

          {roleMembers.length === 1 ? (
            <RadioGroup
              value={roleSingleChoice}
              onChange={(e) => setRoleSingleChoice(e.target.value as 'exiting' | 'remaining')}
            >
              <FormControlLabel
                value="remaining"
                control={<Radio size="small" sx={{ color: highlightColor, '&.Mui-checked': { color: highlightColor } }} />}
                label={
                  <Box sx={{ py: 0.5 }}>
                    <Typography variant="body2" fontWeight={700}>Buyer &mdash; keeps the property</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Buys out the other owner and carries the loan alone. Their income fills
                      the borrower card.
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="exiting"
                control={<Radio size="small" sx={{ color: highlightColor, '&.Mui-checked': { color: highlightColor } }} />}
                label={
                  <Box sx={{ py: 0.5 }}>
                    <Typography variant="body2" fontWeight={700}>Seller &mdash; sells their share</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Walks away with the proceeds and a freed name. Their income is not needed
                      for this calculation.
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          ) : (
            <>
              <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem', mb: 0.75 }}>
                Seller &mdash; sells their share
              </Typography>
              <RadioGroup
                value={roleExitingId ?? ''}
                onChange={(e) => {
                  const id = Number(e.target.value);
                  setRoleExitingId(id);
                  // With exactly two people the other side is settled by elimination.
                  if (roleMembers.length === 2) {
                    const other = roleMembers.find((m) => m.Id !== id);
                    setRoleRemainingId(other?.Id ?? null);
                  } else if (roleRemainingId === id) {
                    setRoleRemainingId(null);
                  }
                }}
              >
                {roleMembers.map((member, idx) => (
                  <FormControlLabel
                    key={member.Id}
                    value={member.Id}
                    control={<Radio size="small" sx={{ color: highlightColor, '&.Mui-checked': { color: highlightColor } }} />}
                    label={<Typography variant="body2">{memberLabel(member, idx)}</Typography>}
                  />
                ))}
              </RadioGroup>

              {roleMembers.length > 2 && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem', mb: 0.75 }}>
                    Buyer &mdash; keeps the property
                  </Typography>
                  <RadioGroup
                    value={roleRemainingId ?? ''}
                    onChange={(e) => setRoleRemainingId(Number(e.target.value))}
                  >
                    {roleMembers
                      .filter((member) => member.Id !== roleExitingId)
                      .map((member) => (
                        <FormControlLabel
                          key={member.Id}
                          value={member.Id}
                          control={<Radio size="small" sx={{ color: highlightColor, '&.Mui-checked': { color: highlightColor } }} />}
                          label={<Typography variant="body2">{memberLabel(member, roleMembers.indexOf(member))}</Typography>}
                        />
                      ))}
                  </RadioGroup>
                </>
              )}

              {roleExitingId !== null && roleRemainingId !== null && (
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 1.5 }}>
                  {memberLabel(
                    roleMembers.find((m) => m.Id === roleExitingId)!,
                    roleMembers.findIndex((m) => m.Id === roleExitingId),
                  )} sells their share to {memberLabel(
                    roleMembers.find((m) => m.Id === roleRemainingId)!,
                    roleMembers.findIndex((m) => m.Id === roleRemainingId),
                  )}, who then owns the property alone.
                </Typography>
              )}
            </>
          )}

          {roleMembers.length === 1 && (
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 1.5 }}>
              You can type the other owner&rsquo;s details in by hand, or load a group with
              both people in it.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1 }}>
          <Button onClick={() => setRoleDialogOpen(false)} size="small">Cancel</Button>
          <Button
            size="small"
            variant="contained"
            disabled={roleMembers.length > 1 && (
              roleExitingId === null || roleRemainingId === null || roleExitingId === roleRemainingId
            )}
            sx={{ bgcolor: highlightColor, '&:hover': { bgcolor: '#B86205' } }}
            onClick={confirmRoles}
          >
            Use these roles
          </Button>
        </DialogActions>
      </Dialog>

      {/* Extended-tenure confirmation — crossing the standard cap costs 20 points of LTV */}
      <Dialog
        open={stretchDialogOpen}
        onClose={() => { setStretchDialogOpen(false); setPendingTenure(null); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem' }}>
          Stretch the tenure past {standardTenureCap} years?
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            A tenure running past {standardTenureCap} years (30 years, or age 65 &mdash;
            whichever binds first) moves the loan into the MAS extended-tenure regime.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The maximum LTV drops from 75% to 55%, so the bank lends less and the remaining
            owner has to find the difference in cash and CPF.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1 }}>
          <Button onClick={() => { setStretchDialogOpen(false); setPendingTenure(null); }} size="small">Cancel</Button>
          <Button
            size="small"
            variant="contained"
            sx={{ bgcolor: highlightColor, '&:hover': { bgcolor: '#B86205' } }}
            onClick={() => {
              if (pendingTenure !== null) update('tenure', pendingTenure);
              setPendingTenure(null);
              setStretchDialogOpen(false);
            }}
          >
            Stretch anyway
          </Button>
        </DialogActions>
      </Dialog>

      {/* Formula / breakdown dialog — opened by tapping any result row */}
      {calcDialog && (
        <Dialog open onClose={() => setCalcDialog(null)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', pb: 1 }}>{calcDialog.title}</DialogTitle>
          <DialogContent dividers sx={{ p: 0 }}>
            <Box sx={{ p: 2 }}>
              {calcDialog.rows.map((row, i) =>
                row.isSeparator ? (
                  <Divider key={i} sx={{ my: 1 }} />
                ) : row.note ? (
                  <Typography key={i} variant="caption" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.5, mb: 1 }}>
                    {row.label}
                  </Typography>
                ) : (
                  <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5, gap: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: row.isResult ? 'text.primary' : 'text.secondary',
                        fontWeight: row.isResult ? 700 : 400,
                        flex: 1,
                        fontFamily: row.label.startsWith('=') || row.label.startsWith('Formula') ? 'monospace' : undefined,
                      }}
                    >
                      {row.label}
                    </Typography>
                    {row.value !== '' && (
                      <Typography
                        variant="body2"
                        fontWeight={row.isResult ? 700 : 600}
                        sx={{ color: row.isResult ? highlightColor : 'text.primary', flexShrink: 0, fontFamily: 'monospace' }}
                      >
                        {row.value}
                      </Typography>
                    )}
                  </Box>
                )
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.disabled', flex: 1 }}>Tap any value for its working</Typography>
            <Button onClick={() => setCalcDialog(null)} size="small" sx={{ color: highlightColor }}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Tutorial */}
      <CalculatorTutorial
        controller={tutorial}
        title="Decoupling Calculator Tutorial"
        manualName="Decoupling Calculator"
        steps={DECOUPLING_TUTORIAL_STEPS}
      />

      {/* 2-column layout */}
      {isMobile ? (
        <Box>
          {rightPanel}
          <Divider sx={{ my: 3 }} />
          {leftPanel}
        </Box>
      ) : (
        <Grid container spacing={3} sx={{ alignItems: 'flex-start' }}>
          <Grid item xs={12} md={5}>
            <Box sx={columnScrollSx}>{leftPanel}</Box>
          </Grid>
          <Grid item xs={12} md={7}>
            <Box sx={columnScrollSx}>{rightPanel}</Box>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
